import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { MessageSquare, Clock, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const ConversationsPage = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await api.get('/conversations');
      setConversations(response.data);
    } catch {
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const handleConversationClick = (conv) => {
    if (conv.status === 'active') {
      localStorage.setItem('activeConversation', JSON.stringify({
        conversation_id: conv.conversation_id,
        title: conv.title
      }));
      navigate('/');
      toast.success('Continuing your conversation');
    } else {
      navigate(`/conversation/${conv.conversation_id}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] py-12 px-4 flex items-center justify-center">
        <p className="text-[#A1A1AA]">Loading conversations...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12 px-4" data-testid="conversations-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-light tracking-tighter mb-4" style={{ fontFamily: 'Outfit' }}>
            Your <span className="text-[#8B5CF6]">Conversation History</span>
          </h1>
          <p className="text-lg text-[#A1A1AA]">Review your past conversations and continue where you left off</p>
        </div>
        {conversations.length === 0 ? (
          <Card className="card-surface border-[#27272A]">
            <CardContent className="py-12 text-center">
              <MessageSquare className="w-16 h-16 text-[#A1A1AA] mx-auto mb-4" />
              <p className="text-[#A1A1AA] text-lg">No conversations yet</p>
              <p className="text-[#A1A1AA] text-sm mt-2">Start by sharing what's on your mind</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {conversations.map((conv) => (
              <Card
                key={conv.conversation_id}
                className={`card-surface border-[#27272A] cursor-pointer transition-all duration-300 ${conv.status === 'active' ? 'hover:border-[#8B5CF6]' : 'hover:border-[#3F3F46]'}`}
                onClick={() => handleConversationClick(conv)}
                data-testid={`conversation-${conv.conversation_id}`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg mb-2" style={{ fontFamily: 'Outfit' }}>{conv.title}</CardTitle>
                      <div className="flex items-center gap-4 text-sm text-[#A1A1AA]">
                        <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{formatDate(conv.created_at)}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${conv.status === 'active' ? 'bg-[#10B981] bg-opacity-20 text-[#10B981]' : 'bg-[#A1A1AA] bg-opacity-20 text-[#A1A1AA]'}`}>{conv.status}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#8B5CF6]">
                      {conv.status === 'active' ? (
                        <>
                          <span className="hidden sm:inline">Continue</span>
                          <ArrowRight className="w-5 h-5" />
                        </>
                      ) : (
                        <>
                          <span className="hidden sm:inline">View</span>
                          <ArrowRight className="w-5 h-5 text-[#71717A]" />
                        </>
                      )}
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationsPage;
